import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { listDailyUsageForMonth, listPartners, type WaPartner } from '@/api/partners'

function formatShekels(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`
}

function currentMonthStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function escapeCsvCell(v: string): string {
  if (v.includes('"') || v.includes(',') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}

interface PartnerMonthAgg {
  partner_id: string
  display_name: string
  instanceDays: number
  messagesSent: number
  messagesReceived: number
  costAgorot: number
}

export default function BillingPage() {
  const [month, setMonth] = useState(currentMonthStr)

  const { data: partners = [] } = useQuery({
    queryKey: ['wa-partners'],
    queryFn: listPartners,
  })

  const { data: usage = [], isLoading } = useQuery({
    queryKey: ['wa-daily-usage-month', month],
    queryFn: () => listDailyUsageForMonth(month),
    enabled: !!month && /^\d{4}-\d{2}$/.test(month),
  })

  const nameById = useMemo(() => {
    const m = new Map<string, WaPartner>()
    for (const p of partners) m.set(p.id, p)
    return m
  }, [partners])

  const rows: PartnerMonthAgg[] = useMemo(() => {
    const agg = new Map<
      string,
      { instanceDays: number; messagesSent: number; messagesReceived: number; costAgorot: number }
    >()
    for (const row of usage) {
      const cur = agg.get(row.partner_id) ?? {
        instanceDays: 0,
        messagesSent: 0,
        messagesReceived: 0,
        costAgorot: 0,
      }
      cur.instanceDays += 1
      cur.messagesSent += row.messages_sent
      cur.messagesReceived += row.messages_received
      cur.costAgorot += row.cost_agorot
      agg.set(row.partner_id, cur)
    }

    const withZeros: PartnerMonthAgg[] = partners.map((p) => {
      const a = agg.get(p.id)
      return {
        partner_id: p.id,
        display_name: p.display_name,
        instanceDays: a?.instanceDays ?? 0,
        messagesSent: a?.messagesSent ?? 0,
        messagesReceived: a?.messagesReceived ?? 0,
        costAgorot: a?.costAgorot ?? 0,
      }
    })

    // Partners with usage but missing from wa_partners (edge case)
    for (const pid of agg.keys()) {
      if (!nameById.has(pid)) {
        const a = agg.get(pid)!
        withZeros.push({
          partner_id: pid,
          display_name: `Unknown (${pid.slice(0, 8)}…)`,
          instanceDays: a.instanceDays,
          messagesSent: a.messagesSent,
          messagesReceived: a.messagesReceived,
          costAgorot: a.costAgorot,
        })
      }
    }

    return withZeros.sort((a, b) => a.display_name.localeCompare(b.display_name))
  }, [usage, partners, nameById])

  const grand = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        instanceDays: acc.instanceDays + r.instanceDays,
        messagesSent: acc.messagesSent + r.messagesSent,
        messagesReceived: acc.messagesReceived + r.messagesReceived,
        costAgorot: acc.costAgorot + r.costAgorot,
      }),
      { instanceDays: 0, messagesSent: 0, messagesReceived: 0, costAgorot: 0 }
    )
  }, [rows])

  const exportCsv = () => {
    const headers = ['Partner Name', 'Total Instance-Days', 'Total Messages', 'Total Cost (NIS)']
    const lines = [headers.join(',')]
    for (const r of rows) {
      const totalMessages = r.messagesSent + r.messagesReceived
      const costNis = (r.costAgorot / 100).toFixed(2)
      lines.push(
        [
          escapeCsvCell(r.display_name),
          String(r.instanceDays),
          String(totalMessages),
          escapeCsvCell(costNis),
        ].join(',')
      )
    }
    lines.push(
      [
        escapeCsvCell('Grand total'),
        String(grand.instanceDays),
        String(grand.messagesSent + grand.messagesReceived),
        escapeCsvCell((grand.costAgorot / 100).toFixed(2)),
      ].join(',')
    )
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `billing-${month}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Billing Summary</h1>
          <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
            Aggregated from daily usage for the selected month
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs mb-1" style={{ color: '#9ca3af' }}>
              Month (YYYY-MM)
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-lg px-3 py-2 text-white text-sm"
              style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
            />
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white transition-colors"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #2d3148' }}>
        <table className="min-w-full text-sm" style={{ background: '#1a1d27' }}>
          <thead>
            <tr style={{ background: '#252836', color: '#9ca3af' }}>
              <th className="text-left font-medium px-4 py-3">Partner Name</th>
              <th className="text-left font-medium px-4 py-3">Total Instance-Days</th>
              <th className="text-left font-medium px-4 py-3">Total Messages</th>
              <th className="text-left font-medium px-4 py-3">Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center" style={{ color: '#6b7280' }}>
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading &&
              rows.map((r) => (
                <tr key={r.partner_id} style={{ borderTop: '1px solid #2d3148' }}>
                  <td className="px-4 py-3 text-white font-medium">{r.display_name}</td>
                  <td className="px-4 py-3 font-mono text-gray-300 num">{r.instanceDays}</td>
                  <td className="px-4 py-3 font-mono text-gray-300 num">
                    {r.messagesSent + r.messagesReceived}
                    <span className="text-xs ml-2" style={{ color: '#6b7280' }}>
                      ({r.messagesSent} sent · {r.messagesReceived} recv)
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-200 num">{formatShekels(r.costAgorot)}</td>
                </tr>
              ))}
            {!isLoading && (
              <tr style={{ borderTop: '2px solid #2d3148', background: '#252836' }}>
                <td className="px-4 py-3 text-white font-semibold">Grand total</td>
                <td className="px-4 py-3 font-mono text-white num font-semibold">{grand.instanceDays}</td>
                <td className="px-4 py-3 font-mono text-white num font-semibold">
                  {grand.messagesSent + grand.messagesReceived}
                </td>
                <td className="px-4 py-3 font-mono text-white num font-semibold">
                  {formatShekels(grand.costAgorot)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
